import { FlexCol, FlexRow } from '@/components/common/Flex';
import InputField from '@/components/common/InputField';
import { useErrorToast } from '@/components/common/Toasts';
import { Header, Paragraph, Text } from '@/components/common/Typography';
import { logError } from '@/util/logger';
import { Divider } from '@chakra-ui/react';
import {
    INCOME_SHARING_MODE_OPTIONS,
    useBaseNetIncomeFormProps,
} from '@common/react/useBaseNetIncomeFormProps';
import {
    getPartnerName,
    isSingleplayer,
    useCurrentUser,
} from '@common/react/user';
import { TOASTS } from '@common/toasts';
import { toDollarsWithCents } from '@common/util/currency';
import { isUndefined } from '@common/util/types';
import { useModal } from '@ebay/nice-modal-react';
import { useBudgetOnboardingContext } from './BudgetOnboardingContext';
import { BudgetOnboardingLayout } from './BudgetOnboardingLayout';

export const BudgetOnboardingIncome = () => {
    const { goToNextStep, variant } = useBudgetOnboardingContext();
    const currentUser = useCurrentUser();
    const { partnerInfo } = currentUser;
    const showErrorToast = useErrorToast();
    const singlePlayer = isSingleplayer(currentUser);
    const modal = useModal();

    const {
        monthlyNetIncome,
        setMonthlyNetIncome,
        partnerMonthlyNetIncome,
        setPartnerMonthlyNetIncome,
        incomeSharingMode,
        setIncomeSharingMode,
        submit,
        isSubmitting,
    } = useBaseNetIncomeFormProps({
        onComplete: () => {
            if (variant === 'create') {
                goToNextStep();
            } else {
                modal.remove();
            }
        },
        onError: (e) => {
            logError(e);
            showErrorToast(TOASTS.unexpectedError);
        },
    });

    return (
        <BudgetOnboardingLayout
            onContinue={submit}
            isContinueDisabled={
                isUndefined(monthlyNetIncome) ||
                (!singlePlayer && !partnerMonthlyNetIncome)
            }
            isSubmitting={isSubmitting}
            buttonText={variant === 'create' ? 'Continue' : 'Save'}
            onClickEvent={
                variant === 'update'
                    ? 'Budget Onboarding Income Save Button Pressed'
                    : 'Budget Onboarding Income Continue Button Pressed'
            }
        >
            <Header>What&apos;s your monthly take-home income?</Header>
            <Paragraph color="textMuted" mt={2}>
                We use this information to help you understand how much
                you&apos;re spending, relative to your income. Take-home income
                is what lands in your bank account.
            </Paragraph>

            <FlexCol gap={4} mt={12}>
                {!singlePlayer && (
                    <>
                        <FlexRow align="center" justify="space-between">
                            <Text>How do you view your income?</Text>
                            <InputField
                                name="income-sharing-mode"
                                type="fancySelect"
                                options={INCOME_SHARING_MODE_OPTIONS}
                                value={incomeSharingMode}
                                onChange={({ value }) =>
                                    setIncomeSharingMode(value)
                                }
                                fancySelectOptions={{
                                    isClearable: false,
                                }}
                                w={140}
                            />
                        </FlexRow>
                        <Divider />
                    </>
                )}

                <IncomeInputRow
                    label="Your monthly take-home income"
                    name="monthly-net-income"
                    value={monthlyNetIncome}
                    onChange={setMonthlyNetIncome}
                />

                {!singlePlayer && (
                    <>
                        <IncomeInputRow
                            label={`${getPartnerName(partnerInfo)}'s monthly take-home income`}
                            name="monthly-net-income"
                            value={partnerMonthlyNetIncome}
                            onChange={setPartnerMonthlyNetIncome}
                        />
                        <FlexCol mt={20}>
                            <Divider />
                            <FlexRow
                                align="center"
                                justify="space-between"
                                mt={6}
                            >
                                <Text>
                                    {incomeSharingMode === 'COMBINED'
                                        ? 'Our'
                                        : 'My'}{' '}
                                    total monthly income
                                </Text>
                                <Text>
                                    {toDollarsWithCents(
                                        (monthlyNetIncome || 0) +
                                            (incomeSharingMode === 'COMBINED'
                                                ? partnerMonthlyNetIncome || 0
                                                : 0)
                                    )}
                                </Text>
                            </FlexRow>
                        </FlexCol>
                    </>
                )}
            </FlexCol>
        </BudgetOnboardingLayout>
    );
};

const IncomeInputRow = ({
    label,
    value,
    name,
    onChange,
}: {
    label: string;
    value: number | null | undefined;
    name: string;
    onChange: (value: number) => void;
}) => (
    <FlexRow align="center" justify="space-between">
        <Text>
            {label}{' '}
            <Text as="span" color="textMuted">
                (after tax)
            </Text>
        </Text>
        <InputField
            name={name}
            type="currency"
            value={value}
            onChange={onChange}
            inputProps={{
                variant: 'outline',
                fontSize: 'md',
                textAlign: 'right',
            }}
            inputOptions={{
                min: 0,
            }}
            w={140}
        />
    </FlexRow>
);
